import { handleDynamicFontResize } from "../processFont";
import {
  canvasDeltaToPdfDelta,
  canvasToPdfPoints,
  pdfRectToCanvasRect,
  resizeCanvasRect,
} from "../../../../utils/coordinateUtils";

const resizeSnapshotsByMousePosition = new WeakMap();

export const handleMouseMove = (
  e,
  currentProcessingFile,
  setCurrentProcessingFile,
  pdfContainerRef,
  resizing,
  initialMousePosition,
  initialInputSize,
  inputPosition,
  resizeDirection,
  moving,
  isDrawing,
  startPoint,
  setRect,
  isSelecting,
  freeSelectStartPoint,
  setSelectionRect,
  pageNumber,
) => {
  const scaleFactor =
    currentProcessingFile?.document?.tempFields?.currentDocument?.pdfSize /
    currentProcessingFile?.document?.tempFields?.currentDocument?.actualWidth;

  const containerRect = pdfContainerRef?.current?.getBoundingClientRect();
  if (!containerRect) return;

  const pdfLeft = 0;
  const pdfRight = containerRect.width - 10;
  const pdfBottom = containerRect.height;

  if (resizing) {
    const dx = e.clientX - initialMousePosition.x;
    const dy = e.clientY - initialMousePosition.y;
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};
    const viewport = currentDocument.viewport;
    const resizeBounds = {
      left: pdfLeft,
      top: 0,
      right: Number(viewport?.width) || pdfRight,
      bottom: Number(viewport?.height) || pdfBottom,
    };
    const tempFields = currentProcessingFile?.document?.tempFields || {};
    const selectedFieldNames = new Set(
      [tempFields.selectedFormField, ...(tempFields.selectedExtraFields || [])]
        .filter(Boolean)
        .map((selectedField) => selectedField.name),
    );
    let resizeSnapshot =
      resizeSnapshotsByMousePosition.get(initialMousePosition);

    if (!resizeSnapshot) {
      resizeSnapshot = new Map(
        (tempFields.formFields || [])
          .filter((formField) => selectedFieldNames.has(formField.name))
          .map((formField) => [formField.name, formField]),
      );
      resizeSnapshotsByMousePosition.set(initialMousePosition, resizeSnapshot);
    }

    const initialCanvasRects = Array.from(resizeSnapshot.values()).map(
      (formField) => ({
        name: formField.name,
        rect: pdfRectToCanvasRect(
          formField,
          viewport || currentDocument.actualHeight || 792,
          currentDocument.scaleFactor || scaleFactor || 1,
          currentDocument.pageXMin || 0,
          currentDocument.pageYMin || 0,
        ),
      }),
    );
    const isGroupResize = initialCanvasRects.length > 1;
    const initialGroupRect = isGroupResize
      ? {
          left: Math.min(...initialCanvasRects.map(({ rect }) => rect.left)),
          top: Math.min(...initialCanvasRects.map(({ rect }) => rect.top)),
          width:
            Math.max(
              ...initialCanvasRects.map(({ rect }) => rect.left + rect.width),
            ) - Math.min(...initialCanvasRects.map(({ rect }) => rect.left)),
          height:
            Math.max(
              ...initialCanvasRects.map(({ rect }) => rect.top + rect.height),
            ) - Math.min(...initialCanvasRects.map(({ rect }) => rect.top)),
        }
      : {
          left: inputPosition.x,
          top: inputPosition.y,
          width: initialInputSize.width,
          height: initialInputSize.height,
        };
    const resizedGroupRect = resizeCanvasRect(
      initialGroupRect,
      dx,
      dy,
      resizeDirection,
      resizeBounds,
    );
    const groupScaleX = initialGroupRect.width
      ? resizedGroupRect.width / initialGroupRect.width
      : 1;
    const groupScaleY = initialGroupRect.height
      ? resizedGroupRect.height / initialGroupRect.height
      : 1;

    setCurrentProcessingFile((prevFile) => {
      const prevTempFields = prevFile.document?.tempFields || {};
      const resizeField = (formField) => {
        const initialField = resizeSnapshot.get(formField.name);
        if (!initialField) return formField;

        const initialCanvasRect = pdfRectToCanvasRect(
          initialField,
          viewport || currentDocument.actualHeight || 792,
          currentDocument.scaleFactor || scaleFactor || 1,
          currentDocument.pageXMin || 0,
          currentDocument.pageYMin || 0,
        );
        const nextCanvasRect = isGroupResize
          ? {
              left:
                resizedGroupRect.left +
                (initialCanvasRect.left - initialGroupRect.left) * groupScaleX,
              top:
                resizedGroupRect.top +
                (initialCanvasRect.top - initialGroupRect.top) * groupScaleY,
              width: initialCanvasRect.width * groupScaleX,
              height: initialCanvasRect.height * groupScaleY,
            }
          : resizedGroupRect;
        const nextPdfRect = canvasToPdfPoints(
          nextCanvasRect.left,
          nextCanvasRect.top,
          nextCanvasRect.width,
          nextCanvasRect.height,
          viewport || scaleFactor,
          72,
          currentDocument.actualHeight || 792,
          currentDocument.pageXMin || 0,
          currentDocument.pageYMin || 0,
        );
        const { newFontSize, fontSizes } = handleDynamicFontResize(
          nextPdfRect.width,
          nextPdfRect.height,
          initialField.fontSize || 10,
          initialField.value || "",
          initialField.fontSizes || [],
          initialField.value || "",
        );

        return {
          ...formField,
          width: nextPdfRect.width,
          height: nextPdfRect.height,
          x: nextPdfRect.x,
          y: nextPdfRect.y,
          fontSize: formField.autoFontSize ? newFontSize : formField.fontSize,
          fontSizes,
        };
      };
      const updatedFormFields = (prevTempFields.formFields || []).map(
        resizeField,
      );
      const updatedFieldsByName = new Map(
        updatedFormFields.map((formField) => [formField.name, formField]),
      );

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevTempFields,
            formFields: updatedFormFields,
            selectedExtraFields: (prevTempFields.selectedExtraFields || []).map(
              (selectedField) =>
                updatedFieldsByName.get(selectedField.name) || selectedField,
            ),
            selectedFormField: prevTempFields.selectedFormField
              ? updatedFieldsByName.get(
                  prevTempFields.selectedFormField.name,
                ) || prevTempFields.selectedFormField
              : null,
          },
        },
      };
    });
  } else if (moving) {
    if (
      !inputPosition ||
      !currentProcessingFile?.document?.tempFields?.selectedFormField
    )
      return;
    // Calculate movement delta
    const scaleFactor =
      currentProcessingFile?.document?.tempFields?.currentDocument?.pdfSize /
      currentProcessingFile?.document?.tempFields?.currentDocument?.actualWidth;

    // Translate the visual drag delta back into the PDF coordinate system.
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};
    const { x: dx, y: dy } = canvasDeltaToPdfDelta(
      e.clientX - initialMousePosition.x,
      e.clientY - initialMousePosition.y,
      currentDocument.viewport,
      scaleFactor,
    );

    // Update position while keeping it within document bounds
    const finalX = Math.max(
      0,
      Math.min(
        inputPosition.x + dx,
        currentProcessingFile?.document?.tempFields?.currentDocument
          .actualWidth - initialInputSize.width,
      ),
    );
    const finalY = Math.max(
      0,
      Math.min(
        inputPosition.y + dy,
        currentProcessingFile?.document?.tempFields?.currentDocument
          .actualHeight - initialInputSize.height,
      ),
    );

    if (
      currentProcessingFile?.document?.tempFields?.selectedExtraFields.length >
      0
    ) {
      // Find the parent's original position (before moving)
      const parentOldX =
        currentProcessingFile?.document?.tempFields?.selectedFormField.x;
      const parentOldY =
        currentProcessingFile?.document?.tempFields?.selectedFormField.y;

      // Move all selected fields proportionally
      const updatedFields =
        currentProcessingFile?.document?.tempFields?.selectedExtraFields.map(
          (field) => {
            if (
              field.name ===
              currentProcessingFile?.document?.tempFields?.selectedFormField
                .name
            ) {
              // Replace the parent's copy with the new position
              return {
                ...currentProcessingFile?.document?.tempFields
                  ?.selectedFormField,
                x: finalX,
                y: finalY,
              };
            } else {
              // Convert child's original position to pixels before calculating new position
              const childOldX = field.x;
              const childOldY = field.y;

              // Calculate relative movement based on the parent's movement
              const childDx = childOldX - parentOldX; // Distance from parent before moving
              const childDy = childOldY - parentOldY; // Distance from parent before moving

              // Apply the movement and keep the relative position the same
              const childNewX = finalX + childDx;
              const childNewY = finalY + childDy;

              return {
                ...field,
                x: childNewX,
                y: childNewY,
              };
            }
          },
        );

      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            formFields: prevFile.document.tempFields.formFields.map(
              (f) => updatedFields.find((uf) => uf.name === f.name) || f,
            ),
            selectedExtraFields: updatedFields,
          },
        },
      }));
    } else {
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            formFields: prevFile.document.tempFields.formFields.map((f) =>
              f.name === prevFile?.document.tempFields.selectedFormField?.name
                ? { ...f, x: finalX, y: finalY }
                : f,
            ),
          },
        },
      }));
    }

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          selectedFormField: prevFile.document.tempFields.selectedFormField
            ? {
                ...prevFile.document.tempFields.selectedFormField,
                x: finalX,
                y: finalY,
              }
            : null,
        },
      },
    }));
  } else if (isDrawing && !moving) {
    const containerRect = pdfContainerRef.current.getBoundingClientRect();
    const currentX =
      (e.clientX - containerRect.left) /
      currentProcessingFile?.document?.tempFields?.currentDocument.scaleFactor;
    const currentY =
      (e.clientY - containerRect.top) /
      currentProcessingFile?.document?.tempFields?.currentDocument.scaleFactor;

    let width = currentX - startPoint.x;
    let height = currentY - startPoint.y;

    // Adjust for direction of dragging (invert Y-axis)
    const newX = width < 0 ? currentX : startPoint.x;
    const newY = height < 0 ? currentY : startPoint.y;

    const returnRect = {
      x: newX,
      y: newY,
      width: Math.abs(width),
      height: Math.abs(height),
    };

    const maxRight = width + newX;
    const maxTop = newY + height;

    const isOutsideTop = maxTop + 5 > pdfBottom;
    const isOutsideRight = maxRight > pdfRight;
    const isOutsideBottom = newY < 5;
    const isOutsideLeft = newX < pdfLeft;

    if (isOutsideTop || isOutsideRight || isOutsideBottom || isOutsideLeft)
      return;
    // Update the rectangle state with correct scaling and flipping
    setRect(returnRect);
  } else if (isSelecting && !moving) {
    const containerRect = pdfContainerRef.current.getBoundingClientRect();

    // Use the exact same coordinate calculation as in mouseDown for freeSelectStartPoint
    const startX = e.clientX - containerRect.left;
    const startY = e.clientY - containerRect.top;

    const currentPoint = {
      x:
        startX /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
      y:
        startY /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
    };

    let width = currentPoint.x - freeSelectStartPoint.x;
    let height = currentPoint.y - freeSelectStartPoint.y;

    // Adjust for direction of dragging (invert Y-axis)
    const newX = width < 0 ? currentPoint.x : freeSelectStartPoint.x;
    const newY = height < 0 ? currentPoint.y : freeSelectStartPoint.y;

    // Store selection rectangle in screen coordinate system (same as how fields are displayed)
    const newSelectionRect = {
      x: newX,
      y: newY,
      width: Math.abs(width),
      height: Math.abs(height),
    };

    const maxRight = width + newX;
    const maxTop = newY + height;

    const isOutsideTop = maxTop + 5 > pdfBottom;
    const isOutsideRight = maxRight > pdfRight;
    const isOutsideBottom = newY < 5;
    const isOutsideLeft = newX < pdfLeft;

    if (isOutsideTop || isOutsideRight || isOutsideBottom || isOutsideLeft)
      return;
    // Update the rectangle state with correct scaling and flipping
    setSelectionRect(newSelectionRect);

    //? COMPARE IN DISPLAY COORDINATE SYSTEM INSTEAD
    // Instead of converting selection to field coordinates, convert fields to display coordinates
    // This way we compare in the same coordinate system as visual display
    const displayScaleFactor =
      currentProcessingFile?.document?.tempFields?.currentDocument
        ?.scaleFactor || 1;
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};

    // Selection rectangle is in container coordinates (already divided by scaleFactor)
    // Convert to display pixels for comparison
    const selectionLeft = newSelectionRect.x * displayScaleFactor;
    const selectionTop = newSelectionRect.y * displayScaleFactor;
    const selectionRight =
      (newSelectionRect.x + newSelectionRect.width) * displayScaleFactor;
    const selectionBottom =
      (newSelectionRect.y + newSelectionRect.height) * displayScaleFactor;

    setCurrentProcessingFile((prevFile) => {
      const previousSelection =
        prevFile.document.tempFields.selectedExtraFields || [];
      const otherPagesSelection = previousSelection.filter((field) => {
        const rawPage = field?.page_num ?? field?.page;
        return Number(rawPage) !== pageNumber;
      });

      const currentPageSelection =
        prevFile.document.tempFields.formFields.filter((field) => {
          const rawPage = field?.page_num ?? field?.page;
          const parsedPage = Number(rawPage);

          if (parsedPage !== pageNumber) return false;

          const fieldRect = pdfRectToCanvasRect(
            field,
            currentDocument.viewport || currentDocument.actualHeight || 792,
            displayScaleFactor,
            currentDocument.pageXMin || 0,
            currentDocument.pageYMin || 0,
          );
          const fieldDisplayLeft = fieldRect.left;
          const fieldDisplayTop = fieldRect.top;
          const fieldDisplayRight = fieldRect.left + fieldRect.width;
          const fieldDisplayBottom = fieldRect.top + fieldRect.height;

          return (
            fieldDisplayLeft < selectionRight &&
            fieldDisplayRight > selectionLeft &&
            fieldDisplayTop < selectionBottom &&
            fieldDisplayBottom > selectionTop
          );
        });

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedExtraFields: [
              ...otherPagesSelection,
              ...currentPageSelection,
            ],
          },
        },
      };
    });

    if (currentProcessingFile?.document?.tempFields?.selectedFormField) {
      const selectedField =
        currentProcessingFile?.document?.tempFields?.selectedFormField;

      const fieldRect = pdfRectToCanvasRect(
        selectedField,
        currentDocument.viewport || currentDocument.actualHeight || 792,
        displayScaleFactor,
        currentDocument.pageXMin || 0,
        currentDocument.pageYMin || 0,
      );
      const fieldDisplayLeft = fieldRect.left;
      const fieldDisplayTop = fieldRect.top;
      const fieldDisplayRight = fieldRect.left + fieldRect.width;
      const fieldDisplayBottom = fieldRect.top + fieldRect.height;

      const isInside =
        fieldDisplayLeft < selectionRight &&
        fieldDisplayRight > selectionLeft &&
        fieldDisplayTop < selectionBottom &&
        fieldDisplayBottom > selectionTop;

      if (!isInside) {
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              selectedFormField: null,
            },
          },
        }));
      }
    }
  }
};
